import React, { useState } from 'react';
import { Star, Quote, ArrowLeft } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { mockData } from '../data/mock';
import OptimizedImage from '../components/OptimizedImage';

const Testimonials = () => {
  const { testimonials } = mockData;
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);

  const truncateText = (text, maxLength = 200) => {
    if (text.length <= maxLength) return { text, isTruncated: false };
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return {
      text: truncated.substring(0, lastSpace) + '...',
      isTruncated: true
    };
  };

  return (
    <div className="bg-dark-bg">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/PC5.jpg"
            alt="Testimonials"
            className="w-full h-full object-cover"
            priority={true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center pt-32 pb-20">
          <p className="text-gold text-sm tracking-widest uppercase mb-4">Client Stories</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-silver leading-tight">
            What Our Clients
            <span className="gold-gradient-text block mt-2">Say About Us</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto mt-6">
            Don't just take our word for it. Hear from the clients who trusted us with their most important moments.
          </p>
          {/* Golden Decorative Line */}
          <div className="flex justify-center mt-8">
            <div className="relative w-96 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent shadow-lg" style={{
              boxShadow: '0 0 15px rgba(255, 215, 0, 0.4), 0 0 30px rgba(255, 215, 0, 0.2)'
            }}>
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gold rounded-full shadow-lg" style={{
                boxShadow: '0 0 10px rgba(255, 215, 0, 0.6), 0 0 20px rgba(255, 215, 0, 0.3)'
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => {
              const { text, isTruncated } = truncateText(testimonial.content);
              return (
                <Card key={testimonial.id} className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group hover:scale-105">
                  <div className="p-8 space-y-6 relative">
                    {/* Quote Icon */}
                    <Quote className="text-gold/20 absolute top-4 right-4" size={48} />
                    
                    {/* Rating */}
                    <div className="flex items-center gap-1 relative z-10">
                      {[...Array(testimonial.rating)].map((_, index) => (
                        <Star key={index} className="text-gold fill-current" size={18} />
                      ))}
                    </div>

                    {/* Client Info */}
                    <div className="relative z-10">
                      <h3 className="text-silver font-bold text-lg">{testimonial.name}</h3>
                      <p className="text-gold text-sm">{testimonial.position}</p>
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                        "{text}"
                      </p>
                      {isTruncated && (
                        <button
                          onClick={() => setSelectedTestimonial(testimonial)}
                          className="text-gold hover:text-gold/80 transition-colors mt-3 text-sm font-semibold underline"
                        >
                          Read More
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Full Testimonial Modal */}
      {selectedTestimonial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <div className="relative bg-dark-grey border-2 border-gold max-w-3xl w-full max-h-[80vh] rounded-sm">
            {/* Back Button */}
            <button
              onClick={() => setSelectedTestimonial(null)}
              className="absolute top-6 left-6 text-gold hover:text-gold/80 transition-colors z-10"
            >
              <ArrowLeft size={28} />
            </button>

            {/* Scrollable Content */}
            <div className="overflow-y-auto max-h-[80vh] p-12 pt-20">
              <Quote className="text-gold/20 mb-6" size={64} />
              
              {/* Rating */}
              <div className="flex items-center gap-1 mb-6">
                {[...Array(selectedTestimonial.rating)].map((_, index) => (
                  <Star key={index} className="text-gold fill-current" size={20} />
                ))}
              </div>

              {/* Client Info */}
              <div className="mb-8">
                <h2 className="text-silver font-bold text-2xl mb-2">{selectedTestimonial.name}</h2>
                <p className="text-gold text-lg">{selectedTestimonial.position}</p>
              </div>

              {/* Full Content */}
              <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-line">
                "{selectedTestimonial.content}"
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-32 bg-black border-t border-gold/20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Ready to Create Your Own
              <span className="gold-gradient-text"> Success Story?</span>
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Join our family of satisfied clients and experience the Prestige Events difference.
            </p>
            <a href="/contact">
              <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-10 py-4 text-lg rounded-none transition-all duration-300">
                Get in Touch
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;